import { useCallback } from 'react'
import {
    useNavigation,
    captureException,
    captureMessage
} from '@thriveglobal/thrive-web-core'
import {
    findCredentialConfig,
    usernameAndPasswordSignin,
    processIdpConfig
} from '../utils/handleIdpConfig'
import { SigninResultType } from '@thriveglobal/thrive-web-auth-core'
import { getPostLoginRedirectPath } from '../utils/redirectUtils'
import { IdpConfig } from '../graphql/generated/autogenerated'
import { Avo } from '@thriveglobal/thrive-web-tracking'

interface useSignInWithPasswordType {
    idpConfigs: Array<IdpConfig>
    email: string
    password: string
    setShowErrorAlert: (showErrorAlert: boolean) => void
    passwordInputRef: React.RefObject<HTMLInputElement>
    setPasswordValidationError: (passwordValidationError: boolean) => void
    setEligibilityValidationError: (eligibilityValidationError: boolean) => void
    setInviteRevokedError: (passwordValidationError: boolean) => void
}

function useSignInWithPassword({
    idpConfigs,
    email,
    password,
    setShowErrorAlert,
    passwordInputRef,
    setPasswordValidationError,
    setEligibilityValidationError,
    setInviteRevokedError
}: useSignInWithPasswordType) {
    const navigate = useNavigation()

    const signInWithPassword = useCallback(async (): Promise<boolean> => {
        try {
            const credentialConfig = findCredentialConfig({ idpConfigs })
            if (!credentialConfig) {
                setShowErrorAlert(true)
                captureException(
                    new Error('Signin Method: unable to find credential config')
                )
                return false
            }
            await processIdpConfig(credentialConfig)
            const signInResult = await usernameAndPasswordSignin(
                email,
                password
            )
            if (signInResult.resultType === SigninResultType.success) {
                Avo.signinCompleted({
                    activityType: 'user_signin',
                    featureType: 'identity',
                    method: 'keycloak',
                    userId_: null
                })
                const redirectPath = getPostLoginRedirectPath()
                captureMessage(
                    `User signed in successfully, redirecting to: ${redirectPath}`
                )
                navigate(redirectPath)
            } else if (
                signInResult.resultType === SigninResultType.unauthorized
            ) {
                Avo.signinCompleted({
                    activityType: 'user_signin',
                    featureType: 'identity',
                    method: 'keycloak',
                    userId_: null
                })
                setPasswordValidationError(true)
                passwordInputRef.current?.focus()
            } else {
                // TODO: Cathal to check if this is ever logged, can we delete this code path?
                Avo.signinFailed({
                    activityType: 'user_signin',
                    featureType: 'identity',
                    method: 'keycloak',
                    reason: `Unknown Credential Sign In Error. signInResult.resultType: ${signInResult.resultType}`
                })
                captureException(
                    new Error('Unknown Credential Sign In Error'),
                    {
                        message: `Unknown Credential Sign In Error. signInResult.resultType: ${signInResult.resultType}`
                    }
                )
                return false
            }
        } catch (error) {
            if (error.message?.includes('User ineligible')) {
                Avo.signinCompleted({
                    activityType: 'user_signin',
                    featureType: 'identity',
                    method: 'keycloak',
                    userId_: null
                })
                setEligibilityValidationError(true)
            } else if (error.message?.includes('Invite revoked')) {
                Avo.signinCompleted({
                    activityType: 'user_signin',
                    featureType: 'identity',
                    method: 'keycloak',
                    userId_: null
                })
                setInviteRevokedError(true)
            } else {
                Avo.signinFailed({
                    activityType: 'user_signin',
                    featureType: 'identity',
                    method: 'keycloak',
                    reason: 'signInWithPassword failed with uncaught error'
                })
                captureException(error, {
                    message: 'signInWithPassword failed with uncaught error'
                })
                return false
            }
        }
        return true
    }, [
        idpConfigs,
        email,
        password,
        setShowErrorAlert,
        passwordInputRef,
        setPasswordValidationError,
        setEligibilityValidationError,
        setInviteRevokedError
    ])
    return signInWithPassword
}

export default useSignInWithPassword
